/**
 * Money, in whole cents, always.
 *
 * Nothing in this system stores a float dollar amount. Every amount is an
 * integer number of cents, and every division goes through `allocateByWeight`
 * so a set of allocations sums to its total exactly.
 *
 * The reason is narrow and specific: the Application Value screen tells a
 * client what they spent. If the per-project numbers do not add up to the
 * invoice they were sent, the screen is not a rounding curiosity - it is a
 * credibility problem in a conversation about money.
 */

/** An integer number of cents. Never a float, never a dollar. */
export type Cents = number;

export function assertCents(value: unknown, label = 'amount'): Cents {
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new TypeError(`${label} must be a whole number of cents, received ${JSON.stringify(value)}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`${label} exceeds the safe integer range`);
  }
  return value;
}

export function assertNonNegativeCents(value: unknown, label = 'amount'): Cents {
  const cents = assertCents(value, label);
  if (cents < 0) throw new RangeError(`${label} must not be negative, received ${cents}`);
  return cents;
}

/** `123456` -> `"$1,234.56"`. Display only. */
export function formatCents(cents: Cents, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(assertCents(cents) / 100);
}

const DECIMAL_AMOUNT = /^([+-]?)(\d*)(?:\.(\d*))?$/;

/**
 * `"1,234.56"` or `1234.56` -> `123456`. Rounds half away from zero.
 *
 * ## Why this does not simply multiply by 100
 *
 * The obvious implementation is `Math.round(n * 100)`, and it is wrong for
 * amounts a person would actually type. Scaling happens in binary floating
 * point, so a value whose exact decimal ends in a half-cent can land just under
 * `.5` and round down:
 *
 *   1.005 * 100 === 100.49999999999999   ->  100 cents, should be 101
 *   8.165 * 100 === 816.4999999999999    ->  816 cents, should be 817
 *   4.475 * 100 === 447.49999999999994   ->  447 cents, should be 448
 *
 * One cent, on the front door for turning a human-entered amount into the
 * integer everything downstream trusts.
 *
 * So the digits are read decimally instead. A number is first rendered with
 * `toFixed(3)`, which produces a plain decimal string with one more place than
 * a cent needs; the third place is then rounded into the second by comparing a
 * character, where there is no float to be off by anything.
 *
 * Found in review — along with the observation that the test claiming to cover
 * this asserted `0.1 + 0.2`, which is the one floating-point case that happened
 * to work.
 */
export function parseDollarsToCents(input: string | number): Cents {
  let text: string;

  if (typeof input === 'number') {
    if (!Number.isFinite(input)) {
      throw new TypeError(`Cannot read ${JSON.stringify(input)} as an amount`);
    }
    text = input.toFixed(3);
  } else {
    text = input.replace(/[$,\s]/g, '');
  }

  const match = DECIMAL_AMOUNT.exec(text);
  const whole = match?.[2] ?? '';
  const fractionRaw = match?.[3] ?? '';

  // A lone sign, an empty string or a bare "." are all not amounts.
  if (!match || (whole === '' && fractionRaw === '')) {
    throw new TypeError(`Cannot read ${JSON.stringify(input)} as an amount`);
  }

  const fraction = `${fractionRaw}000`.slice(0, 3);
  const magnitude = Number(whole || '0') * 100 + Number(fraction.slice(0, 2));
  const rounded = magnitude + (Number(fraction[2]) >= 5 ? 1 : 0);

  return assertCents(match[1] === '-' ? -rounded : rounded, 'amount');
}

/** Minutes at an hourly rate, rounded half-up to the cent. */
export function minutesAtHourlyRate(minutes: number, hourlyRateCents: Cents): Cents {
  if (!Number.isInteger(minutes) || minutes < 0) {
    throw new RangeError(`minutes must be a non-negative whole number, received ${minutes}`);
  }
  assertNonNegativeCents(hourlyRateCents, 'hourlyRateCents');
  return Math.round((minutes * hourlyRateCents) / 60);
}

export interface Weighted {
  /** Stable identifier. Ordering is by this value, so it must not change. */
  id: string;
  /** Relative share. Non-negative. Units are arbitrary but must be consistent. */
  weight: number;
}

export interface Allocation {
  id: string;
  cents: Cents;
}

/**
 * Split `totalCents` across `items` in proportion to their weights, such that
 * the parts sum to the total EXACTLY.
 *
 * The rule, from the requirements document: floor every share, then give the
 * entire remainder to the last item by stable ID.
 *
 * A largest-remainder method would spread the leftover more evenly, and it was
 * considered. The deterministic rule won because the property that matters is
 * reproducibility: the same invoice re-allocated next month must produce the
 * same numbers, or a client sees a figure change with no event behind it. The
 * cost is that one project absorbs up to (n-1) cents. On an invoice measured in
 * thousands of dollars that is invisible; a number that moves on its own is not.
 *
 * Sorting is by `id`, not by input order, so the outcome does not depend on how
 * the caller happened to query.
 *
 * - Empty `items` throws. There is no sensible allocation of money to nobody.
 * - All-zero weights split as evenly as whole cents allow, remainder to the last.
 * - A negative weight throws rather than quietly inverting a share.
 */
export function allocateByWeight(totalCents: Cents, items: readonly Weighted[]): Allocation[] {
  assertNonNegativeCents(totalCents, 'totalCents');

  if (items.length === 0) {
    throw new RangeError('allocateByWeight requires at least one item');
  }

  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item.id)) throw new RangeError(`Duplicate allocation id "${item.id}"`);
    seen.add(item.id);
    if (!Number.isFinite(item.weight) || item.weight < 0) {
      throw new RangeError(`Weight for "${item.id}" must be finite and non-negative, received ${item.weight}`);
    }
  }

  const ordered = [...items].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const totalWeight = ordered.reduce((sum, item) => sum + item.weight, 0);

  const shares: Allocation[] =
    totalWeight === 0
      ? ordered.map((item) => ({ id: item.id, cents: Math.floor(totalCents / ordered.length) }))
      : ordered.map((item) => ({
          id: item.id,
          cents: Math.floor((totalCents * item.weight) / totalWeight),
        }));

  const distributed = shares.reduce((sum, share) => sum + share.cents, 0);
  const remainder = totalCents - distributed;

  const last = shares[shares.length - 1];
  if (last) last.cents += remainder;

  return shares;
}

export interface RoiRange {
  /** Spend the ROI is measured against. */
  spendCents: Cents;
  lowRatio: number | null;
  highRatio: number | null;
  /**
   * The date spend was accumulated through. ALWAYS render this next to the
   * ratio. ROI here divides a forward-looking benefit by spend-to-date, so the
   * number falls every month as costs accumulate, and a client who reads it
   * without the cutoff will think the project is getting worse.
   */
  spendThrough: string;
}

/**
 * Projected ROI as a ratio: (expected benefit - spend) / spend.
 *
 * Returns nulls rather than Infinity when there is no spend yet. A screen that
 * renders "Infinity% return" has failed, and a null is something the UI is
 * forced to handle deliberately.
 */
export function projectedRoi(params: {
  spendCents: Cents;
  benefitLowCents: Cents | null;
  benefitHighCents: Cents | null;
  spendThrough: string;
}): RoiRange {
  const spendCents = assertNonNegativeCents(params.spendCents, 'spendCents');
  const ratio = (benefit: Cents | null): number | null => {
    if (benefit === null || spendCents === 0) return null;
    return (assertCents(benefit, 'benefit') - spendCents) / spendCents;
  };
  return {
    spendCents,
    lowRatio: ratio(params.benefitLowCents),
    highRatio: ratio(params.benefitHighCents),
    spendThrough: params.spendThrough,
  };
}

/** `0.42` -> `"+42%"`, `-0.1` -> `"-10%"`, `null` -> `"—"`. */
export function formatRatio(ratio: number | null): string {
  if (ratio === null) return '\u2014';
  const pct = Math.round(ratio * 100);
  return `${pct >= 0 ? '+' : ''}${pct}%`;
}
